import { Card } from "@/components/ui/card/Card";
import { FLOW_ITEMS } from "./constants";
import type { FlowItemVariant } from "./types";

const itemContainerStyles: Record<FlowItemVariant, string> = {
  neutral: "bg-gray-50  text-text-primary",
  negative: "bg-red-50/70 border border-red-100 text-red-700",
  positive: "bg-green-50/70 border border-green-100 text-green-700",
  highlight: "bg-black text-white",
};

const labelStyles: Record<FlowItemVariant, string> = {
  neutral: "text-text-secondary font-normal text-sm sm:text-base",
  negative: "text-red-600 font-normal text-sm sm:text-base",
  positive: "text-green-600 font-normal text-sm sm:text-base",
  highlight: "text-white font-normal text-sm sm:text-base",
};

const valueStyles: Record<FlowItemVariant, string> = {
  neutral: "text-text-primary font-semibold text-base sm:text-lg",
  negative: "text-red-600 font-semibold text-base sm:text-lg",
  positive: "text-green-600 font-semibold text-base sm:text-lg",
  highlight: "text-[] font-semibold text-base sm:text-lg",
};

export function CashFlowInsight() {
  return (
    <section id="product" className="w-full bg-section scroll-mt-24">
      <div className="w-full max-w-7xl mx-auto px-5 md:px-8 py-20 sm:py-28 flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
        {/* ── Left Column ────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col gap-6 text-center lg:text-left">
          <h2 className="text-3xl sm:text-4xl text-text-primary leading-[1.2] tracking-tight">
            Most cash flow problems do not start with spending too much. They
            start with spending money that was never really yours.
          </h2>

          <p className="text-base sm:text-lg text-text-secondary leading-relaxed max-w-120">
            VAT can sit inside your bank balance and create a false sense of
            cash. By the time the bill is due, the money may already be gone.
            Repayas helps make that hidden liability visible before it becomes a
            problem.
          </p>
        </div>

        {/* ── Right Column: Interactive Deductions Diagram ───────────────── */}
        <div className="w-full lg:w-115 shrink-0">
          <Card className="shadow-lg border border-gray-100/50 p-8 sm:p-10 flex flex-col items-center gap-0 w-full select-none">
            {FLOW_ITEMS.map((item, index) => (
              <div
                key={item.label}
                className="w-full flex flex-col items-center"
              >
                {/* Deduction Bar Box */}
                <div
                  className={`w-full rounded-2xl px-6 py-4.5 flex justify-between items-center shadow-xs transition-transform duration-200 hover:scale-[1.01] ${
                    itemContainerStyles[item.variant]
                  }`}
                >
                  <span className={labelStyles[item.variant]}>
                    {item.label}
                  </span>
                  <span className={valueStyles[item.variant]}>
                    {item.value}
                  </span>
                </div>

                {/* Arrow spacer (if not the last block) */}
                {index < FLOW_ITEMS.length - 1 && (
                  <div
                    className="py-2.5 flex items-center justify-center"
                    aria-hidden="true"
                  >
                    <svg
                      width="14"
                      height="16"
                      viewBox="0 0 14 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-gray-400"
                    >
                      <path
                        d="M7 1v14M7 15l-5-5M7 15l5-5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </Card>
        </div>
      </div>
    </section>
  );
}

export default CashFlowInsight;
